import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, TrendingUp, Users, AlertCircle, ArrowRight,
  Plus, Clock, Building2, FolderOpen, FileText, Send, Bell,
  Briefcase, CreditCard, Mail, CheckCircle, Eye
} from 'lucide-react';
import {
  getAdminDealStats, getDeals, getProjects, getDashboardOverview,
  nudgeSignature, sendEngagementLink
} from '../../services/adminDealFlowApi';
import { Card } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Progress } from '../../components/new-components/ui/progress';
import toast from 'react-hot-toast';

interface DashboardStats {
  fundsUnderManagement: number;
  totalDistributions: number;
  activeInvestors: number;
  activeCapitalCalls: any[];
  actionItems: any[];
}

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const formatDate = (d: string) => d ? new Date(d).toLocaleDateString() : '—';

const AdminDealDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [deals, setDeals] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [overview, setOverview] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, dealsRes, projRes, overviewRes]: any[] = await Promise.all([
          getAdminDealStats(),
          getDeals({ status: 'open' }),
          getProjects(),
          getDashboardOverview(),
        ]);
        setStats(statsRes.data.data);
        setDeals(dealsRes.data.data || []);
        setProjects(projRes.data.data || []);
        setOverview(overviewRes.data?.data || overviewRes.data || null);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleNudge = async (id: string) => {
    try {
      await nudgeSignature(id);
      toast.success('Nudge email sent');
    } catch { toast.error('Failed to send nudge'); }
  };

  const handleResendEngagement = async (id: string) => {
    try {
      await sendEngagementLink(id);
      toast.success('Engagement link resent');
    } catch { toast.error('Failed to resend'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const engagements = overview?.engagements || { items: [], total: 0, active: 0 };
  const signatures = overview?.signatures || { items: [], total: 0, awaiting: 0, signed: 0 };
  const offerings = overview?.offerings || { items: [], total: 0, active: 0 };
  const transactions = overview?.transactions || { items: [] };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-semibold text-[#242424]">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Platform overview and key metrics</p>
      </div>

      {/* KPI Cards */}
      <Card className="p-6 lg:p-8 border-border bg-[#F0F0F0]">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Funds Under Management</p>
            <p className="text-3xl lg:text-4xl font-display font-semibold text-[#242424]">
              {formatCurrency(stats?.fundsUnderManagement || 0)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Total Distributions Paid</p>
            <p className="text-3xl lg:text-4xl font-display font-semibold text-[hsl(var(--success))]">
              {formatCurrency(stats?.totalDistributions || 0)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Active Investors</p>
            <p className="text-3xl lg:text-4xl font-display font-semibold text-[hsl(var(--chart-2))]">
              {stats?.activeInvestors || 0}
            </p>
          </div>
        </div>
      </Card>

      {/* Active Capital Calls */}
      {stats?.activeCapitalCalls && stats.activeCapitalCalls.length > 0 && (
        <Card className="p-6 border-border">
          <h2 className="text-lg font-display font-semibold text-[#242424] mb-4">Active Capital Calls</h2>
          <div className="space-y-4">
            {stats.activeCapitalCalls.map((cc: any) => (
              <div key={cc.id} className="flex items-center gap-6 p-4 bg-[#F0F0F0] rounded-lg">
                <div className="w-12 h-12 bg-background rounded-lg flex items-center justify-center border border-border flex-shrink-0">
                  <Building2 className="h-6 w-6 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">{cc.deal_title}</p>
                  <p className="text-base font-semibold text-[#242424]">{cc.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Progress value={Number(cc.funded_percent)} className="h-2 flex-1" />
                    <span className="text-xs font-semibold text-[#242424]">{cc.funded_percent}%</span>
                  </div>
                </div>
                <div className="text-center px-3 hidden md:block">
                  <p className="text-xs text-muted-foreground">Deadline</p>
                  <p className="text-sm font-medium text-[#242424]">{cc.deadline ? new Date(cc.deadline).toLocaleDateString() : '—'}</p>
                </div>
                <div className="text-center px-3 hidden md:block">
                  <p className="text-xs text-muted-foreground">Call Amount</p>
                  <p className="text-sm font-medium text-[#242424]">{formatCurrency(cc.call_amount)}</p>
                </div>
                <div className="text-center px-3 hidden md:block">
                  <p className="text-xs text-muted-foreground">Received</p>
                  <p className="text-sm font-medium text-[hsl(var(--success))]">{formatCurrency(cc.funded)}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Row: Engagements + Offerings + E-Signatures Awaiting */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Engagements */}
        <Card className="p-6 border-border">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-display font-semibold text-[#242424]">Engagements</h2>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">{engagements.active} active</Badge>
              <button onClick={() => navigate('/admin/engagements')} className="text-sm text-[hsl(var(--chart-2))] hover:underline">View all</button>
            </div>
          </div>
          <div className="space-y-2">
            {engagements.items.slice(0, 10).map((e: any) => (
              <div key={e.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F0F0F0] border border-border transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#242424] truncate">{e.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{e.contact_email || e.contact_name || '—'}</p>
                </div>
                <Badge variant="secondary" className={`text-xs ${e.status === 'active' ? 'bg-green-50 text-green-700' : e.status === 'draft' ? 'bg-gray-50 text-gray-600' : ''}`}>
                  {e.status}
                </Badge>
                {e.status === 'active' && (
                  <button onClick={() => handleResendEngagement(e.id)} title="Resend link email" className="p-1 hover:bg-[#F0F0F0] rounded">
                    <Mail className="h-4 w-4 text-blue-600" />
                  </button>
                )}
              </div>
            ))}
            {engagements.items.length === 0 && (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground">No engagements yet</p>
              </div>
            )}
            <Button variant="outline" className="w-full" onClick={() => navigate('/admin/engagements')}>
              <Plus className="h-4 w-4 mr-2" /> Add New
            </Button>
          </div>
        </Card>

        {/* Active Offerings */}
        <Card className="p-6 border-border">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-display font-semibold text-[#242424]">Offerings</h2>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">{offerings.active} active</Badge>
              <button onClick={() => navigate('/admin/offerings')} className="text-sm text-[hsl(var(--chart-2))] hover:underline">View all</button>
            </div>
          </div>
          <div className="space-y-2">
            {offerings.items.slice(0, 10).map((o: any) => (
              <button key={o.id} onClick={() => navigate(`/admin/offerings`)}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-[#F0F0F0] border border-border transition-colors text-left">
                <div className="w-10 h-10 bg-[#F0F0F0] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-[hsl(var(--chart-2))]">{o.title?.substring(0, 2).toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#242424] truncate">{o.title}</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className={`text-xs mt-1 ${o.status === 'open' ? 'bg-green-50 text-green-700' : o.status === 'draft' ? 'bg-gray-50 text-gray-600' : ''}`}>
                      {o.status === 'open' ? 'Active' : o.status}
                    </Badge>
                    {o.raise_target_cents > 0 && (
                      <span className="text-xs text-muted-foreground mt-1">{formatCurrency(o.raise_target_cents / 100)} target</span>
                    )}
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </button>
            ))}
            {offerings.items.length === 0 && (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground">No offerings yet</p>
              </div>
            )}
            <Button variant="outline" className="w-full" onClick={() => navigate('/admin/offerings')}>
              <Plus className="h-4 w-4 mr-2" /> Add New
            </Button>
          </div>
        </Card>

        {/* E-Signatures Awaiting */}
        <Card className="p-6 border-border">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-display font-semibold text-[#242424]">E-Signatures</h2>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs bg-amber-50 text-amber-700">{signatures.awaiting} awaiting</Badge>
              <button onClick={() => navigate('/admin/esignatures')} className="text-sm text-[hsl(var(--chart-2))] hover:underline">View all</button>
            </div>
          </div>
          <div className="space-y-2">
            {signatures.items.slice(0, 10).map((s: any) => (
              <div key={s.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F0F0F0] border border-border transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#242424] truncate">{s.template_name || 'Untitled'}</p>
                  <p className="text-xs text-muted-foreground truncate">{s.signer_email || '—'}</p>
                </div>
                <Badge variant="secondary" className={`text-xs ${
                  s.status === 'viewed' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'
                }`}>
                  {s.status === 'viewed' && <Eye className="h-3 w-3 mr-1 inline" />}
                  {s.status !== 'viewed' && <Clock className="h-3 w-3 mr-1 inline" />}
                  {s.status}
                </Badge>
                <button onClick={() => handleNudge(s.id)} title="Send nudge email" className="p-1 hover:bg-[#F0F0F0] rounded">
                  <Bell className="h-4 w-4 text-amber-600" />
                </button>
              </div>
            ))}
            {signatures.items.length === 0 && (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground">No signatures awaiting</p>
              </div>
            )}
            <Button variant="outline" className="w-full" onClick={() => navigate('/admin/esignatures')}>
              <Send className="h-4 w-4 mr-2" /> Send for Signature
            </Button>
          </div>
        </Card>
      </div>

      {/* Transactions */}
      <Card className="p-6 border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-display font-semibold text-[#242424]">Recent Transactions</h2>
          </div>
          <button onClick={() => navigate('/admin/transactions')} className="text-sm text-[hsl(var(--chart-2))] hover:underline">View all</button>
        </div>
        {transactions.items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 text-xs text-muted-foreground">ID</th>
                  <th className="text-left p-3 text-xs text-muted-foreground">Type</th>
                  <th className="text-left p-3 text-xs text-muted-foreground">Amount</th>
                  <th className="text-left p-3 text-xs text-muted-foreground">Method</th>
                  <th className="text-left p-3 text-xs text-muted-foreground">Status</th>
                  <th className="text-left p-3 text-xs text-muted-foreground">Date</th>
                </tr>
              </thead>
              <tbody>
                {transactions.items.slice(0, 10).map((t: any) => (
                  <tr key={t.id} className="border-b border-border hover:bg-muted/50 cursor-pointer" onClick={() => navigate('/admin/transactions')}>
                    <td className="p-3 text-sm font-mono text-[#242424]">{t.txn_id || t.id?.substring(0, 8)}</td>
                    <td className="p-3 text-sm text-muted-foreground capitalize">{t.method || t.transaction_type || '—'}</td>
                    <td className="p-3 text-sm font-medium text-[#242424]">
                      {t.amount_cents ? formatCurrency(t.amount_cents / 100) : t.total_amount ? formatCurrency(t.total_amount) : '—'}
                    </td>
                    <td className="p-3">
                      <Badge variant="secondary" className="text-xs uppercase">{t.method || '—'}</Badge>
                    </td>
                    <td className="p-3">
                      <Badge variant="secondary" className={`text-xs ${
                        t.status === 'settled' ? 'bg-green-50 text-green-700' :
                        t.status === 'pending' ? 'bg-amber-50 text-amber-700' :
                        t.status === 'failed' ? 'bg-red-50 text-red-700' :
                        ''
                      }`}>
                        {t.status}
                      </Badge>
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">{formatDate(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-6">
            <CreditCard className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No transactions yet</p>
          </div>
        )}
        <div className="mt-4">
          <Button variant="outline" className="w-full" onClick={() => navigate('/admin/transactions')}>
            <Plus className="h-4 w-4 mr-2" /> New Transfer
          </Button>
        </div>
      </Card>

      {/* Bottom Row: Capital Raises + Projects */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Capital Raises */}
        <Card className="p-6 border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-display font-semibold text-[#242424]">Capital Raises</h2>
            <button onClick={() => navigate('/admin/capital-raises')} className="text-sm text-[hsl(var(--chart-2))] hover:underline">View all</button>
          </div>
          <div className="space-y-3">
            {deals.slice(0, 4).map((deal: any) => (
              <button key={deal.id} onClick={() => navigate(`/admin/capital-raises/${deal.id}`)}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-[#F0F0F0] border border-border transition-colors text-left">
                <div className="w-10 h-10 bg-[#F0F0F0] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-[hsl(var(--chart-2))]">{deal.title?.substring(0, 2).toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#242424] truncate">{deal.title}</p>
                  <Badge variant="secondary" className="text-xs mt-1">
                    {deal.status === 'open' ? 'Active' : deal.status === 'draft' ? 'Draft' : deal.status}
                  </Badge>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </button>
            ))}
            {deals.length === 0 && (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground">No capital raises yet</p>
              </div>
            )}
            <Button variant="outline" className="w-full" onClick={() => navigate('/admin/capital-raises')}>
              <Plus className="h-4 w-4 mr-2" /> Add New
            </Button>
          </div>
        </Card>

        {/* Projects */}
        <Card className="p-6 border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-display font-semibold text-[#242424]">Projects</h2>
            <button onClick={() => navigate('/admin/projects')} className="text-sm text-[hsl(var(--chart-2))] hover:underline">View all</button>
          </div>
          <div className="space-y-3">
            {projects.slice(0, 4).map((proj: any) => (
              <button key={proj.id} onClick={() => navigate(`/admin/projects/${proj.id}`)}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-[#F0F0F0] border border-border transition-colors text-left">
                <div className="w-10 h-10 bg-[#F0F0F0] rounded-lg flex items-center justify-center flex-shrink-0">
                  <FolderOpen className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#242424] truncate">{proj.title}</p>
                  <Badge variant="secondary" className="text-xs mt-1">{proj.status === 'open' ? 'Active' : proj.status}</Badge>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </button>
            ))}
            {projects.length === 0 && (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground">No projects yet</p>
              </div>
            )}
            <Button variant="outline" className="w-full" onClick={() => navigate('/admin/projects')}>
              <Plus className="h-4 w-4 mr-2" /> Add New
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminDealDashboard;
